/**
 * Per-type accent for a notification, shared by the Notifications PAGE and the
 * app-bar bell panel.
 *
 * It lives in its own module because two surfaces render the same rows: when
 * the map was private to the page, the bell had no way to agree with it, and a
 * LEAVE_REJECTED would have been red in one place and grey in the other. One
 * event, one colour, everywhere — the same rule the announcement priority
 * follows.
 *
 * Only the COLOUR lives here. The label is a translation key, because a raw
 * enum ("DOCUMENT_EXPIRY") was once printed to users as if it were prose.
 *
 * What is stored per type is a TONE, not a class pair. The page renders a
 * `<Badge>`, which owns the tint/ink pairing; the bell renders a round icon
 * well, which needs the classes. Deriving both from one tone is what keeps the
 * two surfaces agreeing — and it is what fixes the old pairing, which set
 * `text-info` (the SOLID-fill colour) on `bg-info-surface` and so measured
 * around 1.2:1 in both themes. The `-on-surface` role is the ink for a tint.
 */
export type NotificationTone = 'default' | 'success' | 'warning' | 'danger' | 'info';

export const TYPE_TONE: Record<string, NotificationTone> = {
  // Something needs a decision.
  LEAVE_REQUEST: 'info',
  CORRECTION_REQUEST: 'info',
  // A decision landed.
  LEAVE_APPROVED: 'success',
  CORRECTION_APPROVED: 'success',
  LEAVE_REJECTED: 'danger',
  CORRECTION_REJECTED: 'danger',
  // Something is wrong with the record and someone has to look.
  LATE_ARRIVAL: 'warning',
  MISSED_PUNCH: 'warning',
  DOCUMENT_EXPIRY: 'warning',
  // Informational.
  ANNOUNCEMENT: 'info',
  GENERAL: 'default',
};

/** Tint + ink per tone, for a surface that is NOT a `<Badge>` (the bell's
 * round icon well). Every pair is `bg-*-surface` with `text-*-on-surface`. */
const TONE_SURFACE: Record<NotificationTone, string> = {
  default: 'bg-muted text-muted-foreground',
  success: 'bg-success-surface text-success-on-surface',
  warning: 'bg-warning-surface text-warning-on-surface',
  danger: 'bg-danger-surface text-danger-on-surface',
  info: 'bg-info-surface text-info-on-surface',
};

/**
 * An unknown type keeps its raw string rather than being blanked: a type the
 * server starts sending before the UI knows about it should still be readable.
 */
export function typeTone(type: string): NotificationTone {
  return TYPE_TONE[type] ?? 'default';
}

/** Tint + ink classes for a notification type, for non-Badge surfaces. */
export function typeStyle(type: string): string {
  return TONE_SURFACE[typeTone(type)];
}
